import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import BrandMark from '../../components/BrandMark';
import ToolIcon from '../../components/ToolIcon';
import { useLang } from '../../i18n/context';
import { INSTITUTIONS, fetchInstitutionHoldings } from '../../utils/parse13f';
import { STOCKS, fetchAllStockFundamentals } from '../../utils/stockFundamentals';
import { getSocialCutoff, normalizeSocialPost } from '../socialData';
import { fallbackHeatmapData } from './constants';
import TypewriterText from './TypewriterText';
import LearningPath from './growth/LearningPath';
import CelebrityQuotes from './growth/CelebrityQuotes';
import BookCards from './growth/BookCards';
import ArticleCards from './growth/ArticleCards';
import MarketPanel from './market/MarketPanel';
import SentimentPanel from './sentiment/SentimentPanel';
import HeatmapPanel from './heatmap/HeatmapPanel';
import Holdings13FPanel from './holdings/Holdings13FPanel';
import StockFundamentalsPanel from './stockFundamentals/StockFundamentalsPanel';
import './home.css';

const homeTools = (t) => [
  { title: t('tools.patternTitle'), subtitle: t('tools.patternSubtitle'), desc: t('tools.patternDesc'), path: '/pattern-match', status: t('tools.statusOnline'), tone: 'gold', icon: 'pattern', iconLabel: t('tools.patternTitle') },
  { title: t('tools.detectiveTitle'), subtitle: t('tools.detectiveSubtitle'), desc: t('tools.detectiveDesc'), path: '/dump-detective', status: 'Beta', tone: 'blue', icon: 'detective', iconLabel: t('tools.detectiveTitle') },
  { title: t('tools.levelsTitle'), subtitle: t('tools.levelsSubtitle'), desc: t('tools.levelsDesc'), path: '/support-resistance', status: 'Beta', tone: 'orange', icon: 'levels', iconLabel: t('tools.levelsTitle') },
];
const MARKET_CACHE_KEY = 'home_market_data_v1';
const MARKET_CACHE_TTL = 5 * 60 * 1000;

function readMarketCache() {
  try {
    const raw = sessionStorage.getItem(MARKET_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.timestamp || Date.now() - parsed.timestamp > MARKET_CACHE_TTL) return null;
    return parsed.data && parsed.data.bitcoin ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeMarketCache(data) {
  try {
    sessionStorage.setItem(MARKET_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
  } catch {}
}

export default function HomePage() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  // userStore no longer needed on homepage — login wall removed
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'growth';

  const [marketData, setMarketData] = useState({});
  const [marketLoading, setMarketLoading] = useState(true);
  const marketDataRef = useRef({});
  const [socialPosts, setSocialPosts] = useState([]);
  const [sentiment, setSentiment] = useState(null);
  const [sentimentLoading, setSentimentLoading] = useState(true);
  const [heatmapData, setHeatmapData] = useState(null);
  const [stockQuotes, setStockQuotes] = useState({});
  const [heatmapLoading, setHeatmapLoading] = useState(true);
  const [activeInst, setActiveInst] = useState(0);
  const [holdings13f, setHoldings13f] = useState(null);
  const [holdings13fLoading, setHoldings13fLoading] = useState(false);
  const [holdings13fError, setHoldings13fError] = useState(false);
  const [stockFunds, setStockFunds] = useState(null);
  const [stockFundsLoading, setStockFundsLoading] = useState(true);

  useEffect(() => {
    if (mode !== 'pro') return undefined;
    let cancelled = false;
    const cached = readMarketCache();
    if (cached) {
      marketDataRef.current = cached;
      setMarketData(cached);
      setMarketLoading(false);
    } else {
      setMarketLoading(true);
    }

    const fetchMarket = async () => {
      try {
        const res = await fetch('/api/crypto/markets?limit=100');
        if (!res.ok) throw new Error(`markets ${res.status}`);
        const data = await res.json();
        if (!Array.isArray(data) || !data.length) throw new Error('markets empty');
        const map = {};
        data.forEach(c => { map[c.id] = c; });
        if (!map.bitcoin) throw new Error('bitcoin missing');
        if (!cancelled) {
          marketDataRef.current = map;
          setMarketData(map);
          writeMarketCache(map);
          setMarketLoading(false);
        }
      } catch {
        if (!cancelled) {
          setMarketLoading(Object.keys(marketDataRef.current).length === 0);
        }
      }
    };
    fetchMarket();
    const interval = setInterval(fetchMarket, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [mode]);

  useEffect(() => {
    const fetchSocial = async () => {
      try {
        const { data } = await supabase
          .from('social_posts')
          .select('*')
          .gte('posted_at', getSocialCutoff())
          .order('posted_at', { ascending: false })
          .limit(3);
        setSocialPosts((data || []).map(normalizeSocialPost));
      } catch {}
    };
    fetchSocial();
  }, []);

  useEffect(() => {
    if (mode !== 'pro') return undefined;
    const fetchSentiment = async () => {
      setSentimentLoading(true);
      try {
        const [fngRes, statsRes] = await Promise.all([
          fetch('/api/crypto/fear-greed'),
          fetch('/api/crypto/btc-stats'),
        ]);
        const fngData = await fngRes.json();
        const statsData = await statsRes.json();
        setSentiment({
          fngValue: fngData?.value ?? null,
          fngClassification: fngData?.value_classification || '',
          fngTimestamp: fngData?.timestamp ? new Date(fngData.timestamp * 1000) : null,
          btcDominance: statsData?.btcDominance ?? null,
          totalMcap: statsData?.totalMarketCapUsd ?? null,
        });
      } catch { setSentiment(null); }
      finally { setSentimentLoading(false); }
    };
    fetchSentiment();
    const interval = setInterval(fetchSentiment, 300000);
    return () => clearInterval(interval);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'pro') return undefined;
    const fetchHeatmap = async () => {
      setHeatmapLoading(true);
      try {
        const [cryptoResult, stockResult] = await Promise.allSettled([
          fetch('/api/crypto/markets?limit=100'),
          fetch('/api/stock/quotes?symbols=NVDA,MSTR,COIN,QQQ,TSLA,AAPL'),
        ]);
        if (cryptoResult.status === 'fulfilled' && cryptoResult.value.ok !== false) {
          const data = await cryptoResult.value.json();
          setHeatmapData(Array.isArray(data) && data.length ? data : fallbackHeatmapData);
        } else { setHeatmapData(fallbackHeatmapData); }
        const quotes = {};
        if (stockResult.status === 'fulfilled' && stockResult.value.ok !== false) {
          const stockData = await stockResult.value.json();
          Object.entries(stockData || {}).forEach(([sym, quote]) => { quotes[sym] = quote; });
        }
        setStockQuotes(quotes);
      } catch { setHeatmapData(fallbackHeatmapData); setStockQuotes({}); }
      finally { setHeatmapLoading(false); }
    };
    fetchHeatmap();
    const interval = setInterval(fetchHeatmap, 120000);
    return () => clearInterval(interval);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'pro') return;
    let cancelled = false;
    const fetchHoldings = async () => {
      setHoldings13fLoading(true);
      setHoldings13fError(false);
      try {
        const result = await fetchInstitutionHoldings(INSTITUTIONS[activeInst]);
        if (!cancelled) setHoldings13f(result);
      } catch { if (!cancelled) setHoldings13fError(true); }
      finally { if (!cancelled) setHoldings13fLoading(false); }
    };
    fetchHoldings();
    return () => { cancelled = true; };
  }, [mode, activeInst]);

  useEffect(() => {
    if (mode !== 'pro') return;
    let cancelled = false;
    setStockFundsLoading(true);
    fetchAllStockFundamentals(STOCKS.slice(0, 8).map((stock) => stock.ticker)).then((data) => {
      if (!cancelled) { setStockFunds(data); setStockFundsLoading(false); }
    }).catch(() => { if (!cancelled) { setStockFunds(null); setStockFundsLoading(false); } });
    return () => { cancelled = true; };
  }, [mode]);

  const leftTrackRef = useRef(null);
  const rightTrackRef = useRef(null);
  const marqueeState = useRef({ leftTarget: 40, rightTarget: 37, leftSpeed: 40, rightSpeed: 37, leftPos: 0, rightPos: 0, rightInit: false });

  useEffect(() => {
    let animId, lastTime = performance.now();
    const animate = (time) => {
      const dt = Math.min((time - lastTime) / 1000, 0.1);
      lastTime = time;
      const s = marqueeState.current;
      const t = 1 - Math.exp(-4 * dt);
      const leftTrack = leftTrackRef.current;
      const rightTrack = rightTrackRef.current;
      if (leftTrack) {
        s.leftSpeed += (s.leftTarget - s.leftSpeed) * t;
        s.leftPos -= s.leftSpeed * dt;
        const hw = leftTrack.scrollWidth / 2;
        if (hw > 0 && s.leftPos <= -hw) s.leftPos += hw;
        leftTrack.style.transform = `translateX(${s.leftPos}px)`;
      }
      if (rightTrack) {
        if (!s.rightInit) { const hw = rightTrack.scrollWidth / 2; if (hw > 0) { s.rightPos = -hw; s.rightInit = true; } }
        s.rightSpeed += (s.rightTarget - s.rightSpeed) * t;
        s.rightPos += s.rightSpeed * dt;
        const hw = rightTrack.scrollWidth / 2;
        if (hw > 0 && s.rightPos >= 0) s.rightPos -= hw;
        rightTrack.style.transform = `translateX(${s.rightPos}px)`;
      }
      animId = requestAnimationFrame(animate);
    };
    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, []);

  const handleMarqueeEnter = (key) => { if (key === 'left') marqueeState.current.leftTarget = 16; else marqueeState.current.rightTarget = 15; };
  const handleMarqueeLeave = (key) => { if (key === 'left') marqueeState.current.leftTarget = 40; else marqueeState.current.rightTarget = 37; };
  const handleWordClick = (e) => { const el = e.target; el.classList.add('flash'); setTimeout(() => el.classList.remove('flash'), 600); };
  const copyWechat = () => {
    navigator.clipboard.writeText('admiraltyz');
    const tip = document.getElementById('wechat-copy-tip');
    if (tip) { tip.textContent = lang === 'zh' ? '已复制' : 'Copied'; setTimeout(() => { tip.textContent = t('home.wechatTip'); }, 1500); }
  };

  return (
    <div className={`homepage${mode === 'growth' ? ' hp-growth' : ''}`}>
      {mode === 'growth' && (
      <section id="hero" className="hp-hero">
        <img className="hp-hero-bg-img" src="/images/home-bitcoin-bg.png" alt="" />
        <img className="hp-hero-bg-img hp-hero-bg-img-light" src="/images/home-bitcoin-bg2.png" alt="" />
        <div className="hp-hero-content">
          <h1 className="hp-hero-title">
            GHOST
            <span className="hp-hero-logo"><BrandMark className="hp-hero-logo-img" /></span>
          </h1>
          <p className="hp-hero-subtitle"><TypewriterText text={t('home.growthHeroSubtitle')} /></p>
          <p className="hp-hero-desc">{t('home.growthHeroDesc')}</p>
          <div className="hp-hero-buttons">
            <button className="hp-btn-gold hp-btn-lg" onClick={() => navigate('/docs')}>{t('home.startLearning')}</button>
            <button className="hp-btn-outline hp-btn-lg" onClick={() => navigate('/?mode=pro')}>{t('home.modePro')}</button>
          </div>
        </div>
        <div className="hp-hero-glow" />
      </section>
      )}

      {mode === 'growth' && (
        <>
          <LearningPath navigate={navigate} lang={lang} t={t} />
          <BookCards navigate={navigate} />
          <CelebrityQuotes lang={lang} />
          <ArticleCards navigate={navigate} />
          <section className="hp-cta">
            <h2>{t('home.proTitle')}</h2>
            <p>{t('home.proHint')}</p>
            <button className="hp-btn-gold hp-btn-lg" onClick={() => navigate('/?mode=pro')}>{t('home.modePro')}</button>
          </section>
        </>
      )}

      {mode === 'pro' && (
        <>
          <div className="hp-pro-dashboard hp-pro-dashboard-nav-safe">
            <div className="hp-pro-top-row">
              <MarketPanel marketData={marketData} marketLoading={marketLoading} navigate={navigate} t={t} />
              <SentimentPanel sentiment={sentiment} sentimentLoading={sentimentLoading} lang={lang} t={t} />
            </div>
            <section className="hp-pro-tools-full">
              <div className="hp-pro-panel-head">
                <div><h3>{t('home.proToolsTitle')}</h3><p>{t('home.proToolsDesc')}</p></div>
              </div>
              <div className="hp-pro-tool-grid">
                {homeTools(t).map((tool) => (
                  <button key={tool.path} className={`hp-pro-tool-card ${tool.tone}`} onClick={() => navigate(tool.path)}>
                    <span className="hp-pro-tool-icon"><ToolIcon type={tool.icon} label={tool.iconLabel} /></span>
                    <span className="hp-pro-tool-body">
                      <strong>{tool.title}</strong>
                      <em>{tool.subtitle}</em>
                      <span className="hp-pro-tool-desc">{tool.desc}</span>
                    </span>
                    <span className={`hp-pro-tool-status hp-pro-tool-status-${tool.tone}`}>{tool.status}</span>
                  </button>
                ))}
              </div>
            </section>
            <div className="hp-pro-data-row">
              <HeatmapPanel heatmapData={heatmapData} heatmapLoading={heatmapLoading} stockQuotes={stockQuotes} navigate={navigate} t={t} />
              <Holdings13FPanel activeInst={activeInst} setActiveInst={setActiveInst} holdings13f={holdings13f} holdings13fLoading={holdings13fLoading} holdings13fError={holdings13fError} navigate={navigate} lang={lang} t={t} />
            </div>
            <StockFundamentalsPanel stockFunds={stockFunds} stockFundsLoading={stockFundsLoading} navigate={navigate} lang={lang} t={t} />
            <div className="hp-pro-community-row">
              <section className="hp-pro-airdrop-panel" onClick={() => navigate('/airdrop')}>
                <div className="hp-pro-panel-head">
                  <div><h3>{t('home.proAirdropTitle')}</h3><p>{t('home.proAirdropDesc')}</p></div>
                  <span className="hp-pro-panel-arrow">→</span>
                </div>
              </section>
              {socialPosts.length > 0 && (
                <section className="hp-pro-section">
                  <div className="hp-pro-section-header">
                    <h3>{t('social.sectionTitle')}</h3>
                    <button className="hp-pro-link" onClick={() => navigate('/social')}>{t('social.viewAll')} →</button>
                  </div>
                  <div className="hp-pro-social-grid">
                    {socialPosts.slice(0, 2).map((post) => (
                      <div className="hp-pro-social-card" key={post.id} onClick={() => navigate(post.account_path)}>
                        <div className="hp-pro-social-author">
                          {post.author_avatar ? <img src={post.author_avatar} alt="" /> : <span>{post.author_name.slice(0, 1)}</span>}
                          <strong>{post.author_name}</strong>
                          <em>@{post.author_username}</em>
                        </div>
                        <p>{post.content}</p>
                        <span>{new Date(post.posted_at).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
            <div className="hp-pro-teaser-inline">
              <div className="hp-pro-teaser-badge">PRO</div>
              <span>{t('home.proDesc')}</span>
              <button className="hp-pro-teaser-btn" onClick={copyWechat}>{t('home.proCta')}</button>
            </div>
          </div>
        </>
      )}

      <section className="hp-marquee-section">
        <div className="hp-marquee-row" onMouseEnter={() => handleMarqueeEnter('left')} onMouseLeave={() => handleMarqueeLeave('left')}>
          <div ref={leftTrackRef} className="hp-marquee-track">
            {[...Array(2)].map((_, i) => (
              <React.Fragment key={i}>
                {t('home.marqueeRow1').split(/\s+/).map((word, j) => (
                  <span key={j} className="hp-marquee-word" onClick={handleWordClick}>{word}</span>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="hp-marquee-row" onMouseEnter={() => handleMarqueeEnter('right')} onMouseLeave={() => handleMarqueeLeave('right')}>
          <div ref={rightTrackRef} className="hp-marquee-track">
            {[...Array(2)].map((_, i) => (
              <React.Fragment key={i}>
                {t('home.marqueeRow2').split(/\s+/).map((word, j) => (
                  <span key={j} className="hp-marquee-word" onClick={handleWordClick}>{word}</span>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
